import React, { useEffect, useState } from "react";
import { api } from "../../api/client";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "../ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  MODULES, PRIORITIES, OBJECT_TYPES as FALLBACK_OBJECT_TYPES, BRD_STATUSES, FDD_READY, SIT_STATUSES,
  UAT_STATUSES, PROD_MIGRATION_STATUSES, DEV_STATUSES, OVERALL_STATUSES, RISK_LEVELS,
} from "../../lib/constants";
import AuditTrail from "./AuditTrail";
import { LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

async function copyTaskLinkFromInitial(initial) {
  if (!initial || typeof window === "undefined") return;
  const params = new URLSearchParams();
  if (initial.project_id) params.set("project_id", initial.project_id);
  params.set("focus", initial.id);
  if (initial.object_id) params.set("search", initial.object_id);
  const url = `${window.location.origin}/?${params.toString()}`;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(url);
    } else {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus(); ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success(`Link to ${initial.object_id || "task"} copied`, { description: url });
  } catch {
    toast.error("Could not copy link");
  }
}

const EMPTY = {
  module: "", object_id: "", object_name: "", object_desc: "", title: "",
  object_type: "", priority: "", functional_owner: "", resource: "",
  brd_status: "", brd_date: "", fdd_ready: "", fdd_released_date: "", fdd_signoff_date: "",
  sit_status: "", sit_completion_date: "", uat_status: "", uat_completion_date: "",
  prod_migration: "", prod_migration_date: "",
  technical_resource: "", planned_dev_days: "", dev_start_date: "", dev_end_date: "", target_date: "",
  development_status: "", actual_start_date: "", actual_end_date: "", overall_status: "", completion_pct: "",
  risk_level: "", risk_description: "", delay_days: "", scope_of_works: "", comments: "",
};

function Field({ id, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[10px] uppercase tracking-[0.2em] font-semibold text-neutral-600">
        {label}
      </Label>
      {children}
    </div>
  );
}

function SelectField({ id, value, options, onChange, placeholder }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger id={id} data-testid={`form-${id}`} className="h-10 bg-white">
        <SelectValue placeholder={placeholder || "Select..."} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function TaskForm({ open, onOpenChange, initial, onSubmit }) {
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [objectTypes, setObjectTypes] = useState(FALLBACK_OBJECT_TYPES);

  useEffect(() => {
    if (initial) setData({ ...EMPTY, ...initial });
    else setData(EMPTY);
  }, [initial, open]);

  useEffect(() => {
    if (!open) return;
    api.get("/object-types").then((r) => {
      const names = (r.data.object_types || []).map((t) => t.name);
      // Include current value even if it's inactive so the edit form doesn't lose it
      if (initial?.object_type && !names.includes(initial.object_type)) {
        names.push(initial.object_type);
      }
      if (names.length) setObjectTypes(names);
    }).catch(() => setObjectTypes(FALLBACK_OBJECT_TYPES));
  }, [open, initial]);

  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));
  const setInput = (k) => (e) => set(k)(e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...data };
    ["planned_dev_days", "completion_pct", "delay_days"].forEach((k) => {
      if (payload[k] === "" || payload[k] === null) payload[k] = null;
      else payload[k] = Number(payload[k]);
    });
    try {
      await onSubmit(payload);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl p-0 bg-white flex flex-col h-full"
        data-testid="task-form-sheet"
      >
        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#E5E5E5] bg-white shrink-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <SheetTitle
                  className="text-2xl font-black tracking-tight"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                >
                  {initial ? "Edit Task" : "New Task"}
                </SheetTitle>
                <SheetDescription className="text-neutral-500 mt-1">
                  {initial
                    ? `Last updated by ${initial.updated_by || "—"}`
                    : "Create a new deliverable in the tracker."}
                </SheetDescription>
              </div>
              {initial ? (
                <button
                  type="button"
                  onClick={() => copyTaskLinkFromInitial(initial)}
                  data-testid="form-copy-link-btn"
                  className="inline-flex items-center gap-1.5 shrink-0 mt-1 px-2.5 h-8 rounded border border-neutral-200 text-xs text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
                  title="Copy shareable link"
                >
                  <LinkSimple size={13} weight="bold" />
                  Copy link
                </button>
              ) : null}
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
            <Tabs defaultValue="identification" className="px-6 py-4">
              <TabsList className="w-full justify-start overflow-x-auto flex flex-nowrap">
                <TabsTrigger value="identification" data-testid="tab-identification">Identification</TabsTrigger>
                <TabsTrigger value="planning" data-testid="tab-planning">Planning</TabsTrigger>
                <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
                <TabsTrigger value="development" data-testid="tab-development">Development</TabsTrigger>
                <TabsTrigger value="risk" data-testid="tab-risk">Risk</TabsTrigger>
                <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
                {initial ? (
                  <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
                ) : null}
              </TabsList>

            {/* Identification */}
            <TabsContent value="identification" className="pt-4 grid grid-cols-2 gap-4">
              <Field id="module" label="Module">
                <SelectField id="module" value={data.module} options={MODULES} onChange={set("module")} />
              </Field>
              <Field id="object_id" label="Object ID">
                <Input id="object_id" data-testid="form-object_id" value={data.object_id} onChange={setInput("object_id")} />
              </Field>
              <Field id="object_name" label="Object Name">
                <Input id="object_name" data-testid="form-object_name" value={data.object_name} onChange={setInput("object_name")} />
              </Field>
              <Field id="object_type" label="Object Type">
                <SelectField id="object_type" value={data.object_type} options={objectTypes} onChange={set("object_type")} />
              </Field>
              <div className="col-span-2">
                <Field id="title" label="Title">
                  <Input id="title" data-testid="form-title" value={data.title} onChange={setInput("title")} />
                </Field>
              </div>
              <div className="col-span-2">
                <Field id="object_desc" label="Object Description">
                  <Textarea id="object_desc" data-testid="form-object_desc" rows={2} value={data.object_desc} onChange={setInput("object_desc")} />
                </Field>
              </div>
              <Field id="priority" label="Priority">
                <SelectField id="priority" value={data.priority} options={PRIORITIES} onChange={set("priority")} />
              </Field>
              <Field id="functional_owner" label="Functional Owner">
                <Input id="functional_owner" data-testid="form-functional_owner" value={data.functional_owner} onChange={setInput("functional_owner")} />
              </Field>
              <Field id="resource" label="Resource">
                <Input id="resource" data-testid="form-resource" value={data.resource} onChange={setInput("resource")} />
              </Field>
            </TabsContent>

            {/* Planning */}
            <TabsContent value="planning" className="pt-4 grid grid-cols-2 gap-4">
              <Field id="brd_status" label="BRD Status">
                <SelectField id="brd_status" value={data.brd_status} options={BRD_STATUSES} onChange={set("brd_status")} />
              </Field>
              <Field id="brd_date" label="BRD Date">
                <Input id="brd_date" type="date" data-testid="form-brd_date" value={data.brd_date || ""} onChange={setInput("brd_date")} />
              </Field>
              <Field id="fdd_ready" label="FDD Ready">
                <SelectField id="fdd_ready" value={data.fdd_ready} options={FDD_READY} onChange={set("fdd_ready")} />
              </Field>
              <Field id="fdd_released_date" label="FDD Released Date">
                <Input id="fdd_released_date" type="date" data-testid="form-fdd_released_date" value={data.fdd_released_date || ""} onChange={setInput("fdd_released_date")} />
              </Field>
              <Field id="fdd_signoff_date" label="FDD Sign Off Date">
                <Input id="fdd_signoff_date" type="date" data-testid="form-fdd_signoff_date" value={data.fdd_signoff_date || ""} onChange={setInput("fdd_signoff_date")} />
              </Field>
              <Field id="technical_resource" label="Technical Resource">
                <Input id="technical_resource" data-testid="form-technical_resource" value={data.technical_resource} onChange={setInput("technical_resource")} />
              </Field>
            </TabsContent>

            {/* Schedule */}
            <TabsContent value="schedule" className="pt-4 grid grid-cols-2 gap-4">
              <Field id="sit_status" label="SIT Status">
                <SelectField id="sit_status" value={data.sit_status} options={SIT_STATUSES} onChange={set("sit_status")} />
              </Field>
              <Field id="sit_completion_date" label="SIT Completion Date">
                <Input id="sit_completion_date" type="date" data-testid="form-sit_completion_date" value={data.sit_completion_date || ""} onChange={setInput("sit_completion_date")} />
              </Field>
              <Field id="uat_status" label="UAT Status">
                <SelectField id="uat_status" value={data.uat_status} options={UAT_STATUSES} onChange={set("uat_status")} />
              </Field>
              <Field id="uat_completion_date" label="UAT Completion Date">
                <Input id="uat_completion_date" type="date" data-testid="form-uat_completion_date" value={data.uat_completion_date || ""} onChange={setInput("uat_completion_date")} />
              </Field>
              <Field id="prod_migration" label="Prod Migration">
                <SelectField id="prod_migration" value={data.prod_migration} options={PROD_MIGRATION_STATUSES} onChange={set("prod_migration")} />
              </Field>
              <Field id="prod_migration_date" label="Prod Migration Date">
                <Input id="prod_migration_date" type="date" data-testid="form-prod_migration_date" value={data.prod_migration_date || ""} onChange={setInput("prod_migration_date")} />
              </Field>
            </TabsContent>

            {/* Development */}
            <TabsContent value="development" className="pt-4 grid grid-cols-2 gap-4">
              <Field id="planned_dev_days" label="Planned Dev Days">
                <Input id="planned_dev_days" type="number" step="0.5" data-testid="form-planned_dev_days" value={data.planned_dev_days} onChange={setInput("planned_dev_days")} />
              </Field>
              <Field id="dev_start_date" label="Dev Start Date">
                <Input id="dev_start_date" type="date" data-testid="form-dev_start_date" value={data.dev_start_date || ""} onChange={setInput("dev_start_date")} />
              </Field>
              <Field id="dev_end_date" label="Dev End Date">
                <Input id="dev_end_date" type="date" data-testid="form-dev_end_date" value={data.dev_end_date || ""} onChange={setInput("dev_end_date")} />
              </Field>
              <Field id="target_date" label="Target Date">
                <Input id="target_date" type="date" data-testid="form-target_date" value={data.target_date || ""} onChange={setInput("target_date")} />
              </Field>
              <Field id="actual_start_date" label="Actual Start Date">
                <Input id="actual_start_date" type="date" data-testid="form-actual_start_date" value={data.actual_start_date || ""} onChange={setInput("actual_start_date")} />
              </Field>
              <Field id="actual_end_date" label="Actual End Date">
                <Input id="actual_end_date" type="date" data-testid="form-actual_end_date" value={data.actual_end_date || ""} onChange={setInput("actual_end_date")} />
              </Field>
              <Field id="development_status" label="Development Status">
                <SelectField id="development_status" value={data.development_status} options={DEV_STATUSES} onChange={set("development_status")} />
              </Field>
              <Field id="overall_status" label="Overall Status">
                <SelectField id="overall_status" value={data.overall_status} options={OVERALL_STATUSES} onChange={set("overall_status")} />
              </Field>
              <Field id="completion_pct" label="Completion %">
                <Input id="completion_pct" type="number" min="0" max="100" data-testid="form-completion_pct" value={data.completion_pct} onChange={setInput("completion_pct")} />
              </Field>
            </TabsContent>

            {/* Risk */}
            <TabsContent value="risk" className="pt-4 grid grid-cols-2 gap-4">
              <Field id="risk_level" label="Risk Level">
                <SelectField id="risk_level" value={data.risk_level} options={RISK_LEVELS} onChange={set("risk_level")} />
              </Field>
              <Field id="delay_days" label="Delay (Days)">
                <Input id="delay_days" type="number" data-testid="form-delay_days" value={data.delay_days} onChange={setInput("delay_days")} />
              </Field>
              <div className="col-span-2">
                <Field id="risk_description" label="Risk Description">
                  <Textarea id="risk_description" rows={3} data-testid="form-risk_description" value={data.risk_description} onChange={setInput("risk_description")} />
                </Field>
              </div>
            </TabsContent>

            {/* Notes */}
            <TabsContent value="notes" className="pt-4 grid grid-cols-1 gap-4">
              <Field id="scope_of_works" label="Scope of Works">
                <Textarea id="scope_of_works" rows={4} data-testid="form-scope_of_works" value={data.scope_of_works} onChange={setInput("scope_of_works")} />
              </Field>
              <Field id="comments" label="Comments">
                <Textarea id="comments" rows={4} data-testid="form-comments" value={data.comments} onChange={setInput("comments")} />
              </Field>
            </TabsContent>

            {initial ? (
              <TabsContent value="history" className="pt-4">
                <AuditTrail taskId={initial.id} />
              </TabsContent>
            ) : null}
          </Tabs>
          </div>

          <SheetFooter className="px-6 py-4 border-t border-[#E5E5E5] bg-white flex flex-row justify-end gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="task-form-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="task-form-submit-btn"
              className="bg-[#387EE6] hover:bg-[#2563EB] text-white"
            >
              {saving ? "Saving..." : initial ? "Save Changes" : "Create Task"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
